'use client';

import React, { useState, useMemo } from 'react';
import Image from 'next/image';
import { useBloodVisionIndia } from 'components/blood-vision/BloodVisionIndiaContext';
import { IndiaPlan, IndiaBiomarkerSummary } from './types';
import { getCompressedAssetUrl } from 'lib/config/assetUrls';
import styles from './PlanComparisonTable.module.css';

// Helper function to extract the first value from subtitle (e.g., "60+ Parameters" -> "60+")
// const extractFirstValue = (subtitle: string | undefined | null): string => {
//   if (!subtitle) return '-';
//   const match = subtitle.match(/^[\d+]+\+?/);
//   return match ? match[0] : subtitle.split(' ')[0] || '-';
// };

// Helper function to parse tests from description (comma-separated)
const parseTests = (description: string | undefined | null): string[] => {
  if (!description) return [];
  return description
    .split(',')
    .map((test) => test.trim())
    .filter(Boolean);
};

// Helper function to get unique biomarker categories across all plans
const getUniqueBiomarkerCategories = (plans: IndiaPlan[]): string[] => {
  const categoriesSet = new Set<string>();
  plans.forEach((plan) => {
    if (plan.biomarker_summary) {
      plan.biomarker_summary.forEach((biomarker) => {
        if (biomarker.title) {
          categoriesSet.add(biomarker.title);
        }
      });
    }
  });
  return Array.from(categoriesSet);
};

// Helper function to get biomarker data for a specific category from a plan
const getBiomarkerForCategory = (
  plan: IndiaPlan,
  categoryTitle: string,
): IndiaBiomarkerSummary | undefined => {
  if (!plan.biomarker_summary) return undefined;
  return plan.biomarker_summary.find((b) => b.title === categoryTitle);
};

const CheckIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M15.7734 5.33502L7.26562 14.335L3.39844 10.2441"
      stroke="black"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const CrossIcon = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="20"
    height="20"
    viewBox="0 0 20 20"
    fill="none"
  >
    <path
      d="M14 6L6 14"
      stroke="#B6B6B6"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M6 6L14 14"
      stroke="#B6B6B6"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const ChevronRight = () => (
  <svg
    xmlns="http://www.w3.org/2000/svg"
    width="16"
    height="16"
    viewBox="0 0 16 16"
    fill="none"
  >
    <path
      d="M4 10L8 6L12 10"
      stroke="black"
      strokeWidth="1.33333"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

interface PlanComparisonTableProps {
  selectedGender?: 'male' | 'female' | null;
  visiblePlans?: IndiaPlan[]; // For mobile: only show these plans in comparison
}

const PlanComparisonTable: React.FC<PlanComparisonTableProps> = ({
  selectedGender = 'male',
  visiblePlans,
}) => {
  const { categories } = useBloodVisionIndia();
  const [expandedCategory, setExpandedCategory] = useState<string | null>(null);

  // Get recommended category plans and filter by selected gender
  const recommendedPlans = useMemo(() => {
    const recommendedCategory = categories.find(
      (cat) => cat.id === 'recommended',
    );
    if (!recommendedCategory?.plans) return [];

    // Filter plans based on selected gender
    if (selectedGender === null) return [];

    const filtered = recommendedCategory.plans.filter((plan) => {
      if (selectedGender === 'male') {
        return plan.suitable_for?.male === true;
      } else if (selectedGender === 'female') {
        return plan.suitable_for?.female === true;
      }
      return false;
    });

    // On mobile, if visiblePlans is provided, use only those plans and maintain order
    if (visiblePlans && visiblePlans.length > 0) {
      // Maintain the order of visiblePlans
      return visiblePlans
        .map((vp) => filtered.find((plan) => plan.id === vp.id))
        .filter((plan): plan is IndiaPlan => plan !== undefined);
    }

    return filtered;
  }, [categories, selectedGender, visiblePlans]);

  // Get unique biomarker categories across all recommended plans
  const biomarkerCategories = useMemo(() => {
    return getUniqueBiomarkerCategories(recommendedPlans);
  }, [recommendedPlans]);

  // Build comparison data from real API data
  const comparisonData = useMemo(() => {
    if (recommendedPlans.length === 0) return null;

    // Column headers are plan names
    const columns = recommendedPlans.map((plan) => plan.name);

    // Total tests row - use total_biomarker_count only
    const totalTestsValues = recommendedPlans.map(
      (plan) => plan.total_biomarker_count?.toString() || '-',
    );

    // Build categories data from biomarker_summary
    const categoriesData = biomarkerCategories.map((categoryTitle) => {
      // Get biomarker data for each plan (count from biomarker_summary[i].count)
      const values = recommendedPlans.map((plan) => {
        const biomarker = getBiomarkerForCategory(plan, categoryTitle);
        return biomarker?.count?.toString() || '-';
      });

      // Collect ALL unique biomarkers from ALL plans for this category
      const allTestNamesSet = new Set<string>();
      recommendedPlans.forEach((plan) => {
        const biomarker = getBiomarkerForCategory(plan, categoryTitle);
        if (biomarker?.description) {
          const tests = parseTests(biomarker.description);
          tests.forEach((testName) => {
            allTestNamesSet.add(testName.trim());
          });
        }
      });

      // Convert set to array and sort alphabetically for consistent ordering
      const allUniqueTests = Array.from(allTestNamesSet).sort((a, b) =>
        a.localeCompare(b, undefined, { sensitivity: 'base' }),
      );

      // Build test availability for each plan - check each unique test against all plans
      const testsData = allUniqueTests.map((testName) => {
        const testValues = recommendedPlans.map((plan) => {
          const planBiomarker = getBiomarkerForCategory(plan, categoryTitle);
          if (!planBiomarker?.description) return false;
          // Check if test exists in this plan's description (case-insensitive)
          const descriptionTests = parseTests(planBiomarker.description);
          return descriptionTests.some(
            (descTest) =>
              descTest.trim().toLowerCase() === testName.toLowerCase(),
          );
        });
        return { name: testName, values: testValues };
      });

      // Get icon_url from the first plan that has this category
      const firstPlanWithCategory = recommendedPlans.find((plan) =>
        getBiomarkerForCategory(plan, categoryTitle),
      );
      const biomarkerWithIcon = firstPlanWithCategory
        ? getBiomarkerForCategory(firstPlanWithCategory, categoryTitle)
        : undefined;
      const iconUrl =
        biomarkerWithIcon?.icon_url ||
        getCompressedAssetUrl('/web_v2/site-static/shop/allProductsBg.svg');

      return {
        label: categoryTitle,
        values,
        tests: testsData,
        iconUrl,
      };
    });

    return {
      columns,
      totalTestsValues,
      categories: categoriesData,
    };
  }, [recommendedPlans, biomarkerCategories]);

  const handleCategoryClick = (label: string, hasTests: boolean) => {
    if (!hasTests) return;

    if (expandedCategory === label) {
      setExpandedCategory(null);
    } else {
      setExpandedCategory(label);
    }
  };

  // Don't render if no data
  if (!comparisonData || comparisonData.columns.length === 0) {
    return null;
  }

  const numColumns = comparisonData.columns.length;
  // On mobile (when visiblePlans is provided), use the actual number of visible plans (up to 2)
  const displayColumns =
    visiblePlans && visiblePlans.length > 0
      ? Math.min(visiblePlans.length, 2)
      : numColumns;

  return (
    <section className={styles.comparisonSection}>
      <h2 className={styles.comparisonTitle}>Compare tests</h2>
      <div className={styles.tableContainer}>
        {/* Header row with "Compare Plans" and plan names */}
        <div
          className={`${styles.gridRow} ${styles.headerRow}`}
          style={{ ['--columns']: displayColumns } as React.CSSProperties}
          data-single={displayColumns === 1}
        >
          <span className={styles.headerCell}>Compare Plans</span>
          {comparisonData.columns
            .slice(0, displayColumns)
            .map((column, index) => (
              <span className={styles.headerCell} key={index}>
                {column}
              </span>
            ))}
        </div>

        {/* Total Tests row */}
        <div className={styles.categoryWrapper}>
          <div
            className={`${styles.gridRow} ${styles.dataRow}`}
            style={{ ['--columns']: displayColumns } as React.CSSProperties}
            data-single={displayColumns === 1}
          >
            <div className={styles.rowLabelContainer}>
              <span className={styles.rowLabel}>Total Tests</span>
            </div>
            {comparisonData.totalTestsValues
              .slice(0, displayColumns)
              .map((value, index) => (
                <span className={styles.rowValue} key={index}>
                  {value}
                </span>
              ))}
          </div>
        </div>

        {/* Biomarker category rows */}
        {comparisonData.categories.map((category, index) => {
          const hasTests = category.tests && category.tests.length > 0;
          const isExpanded = expandedCategory === category.label;

          return (
            <div className={styles.categoryWrapper} key={index}>
              <div
                className={`${styles.gridRow} ${styles.dataRow}`}
                style={
                  { ['--columns']: displayColumns } as React.CSSProperties
                }
                data-single={displayColumns === 1}
                data-clickable={hasTests}
                data-expanded={isExpanded}
                onClick={() => handleCategoryClick(category.label, !!hasTests)}
              >
                <div className={styles.rowLabelContainer}>
                  <div className={styles.categoryLabelWrapper}>
                    {category.iconUrl && (
                      <div className={styles.categoryIcon}>
                        <Image
                          src={category.iconUrl}
                          alt={category.label}
                          width={40}
                          height={40}
                        />
                      </div>
                    )}
                    <span className={styles.rowLabel}>{category.label}</span>
                  </div>
                  {/* Chevron - right aligned on mobile, inline on desktop */}
                  {hasTests && (
                    <div
                      className={styles.chevronIcon}
                      data-expanded={isExpanded}
                    >
                      <ChevronRight />
                    </div>
                  )}
                </div>
                {/* Category count values - hidden on mobile */}
                {category.values
                  .slice(0, displayColumns)
                  .map((value, valueIndex) => (
                    <span
                      className={`${styles.rowValue} ${styles.categoryRowValue}`}
                      key={valueIndex}
                    >
                      {value}
                    </span>
                  ))}
              </div>

              {/* Expanded tests */}
              {category.tests && category.tests.length > 0 && (
                <div
                  className={styles.expandedContent}
                  data-expanded={isExpanded}
                >
                  {category.tests.map((test, testIndex) => (
                    <div
                      className={`${styles.gridRow} ${styles.testRow}`}
                      key={testIndex}
                      style={
                        { ['--columns']: displayColumns } as React.CSSProperties
                      }
                      data-single={displayColumns === 1}
                    >
                      <span className={styles.testName}>{test.name}</span>
                      {test.values
                        .slice(0, displayColumns)
                        .map((value, valueIndex) => (
                          <div className={styles.testValue} key={valueIndex}>
                            {value ? <CheckIcon /> : <CrossIcon />}
                          </div>
                        ))}
                    </div>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
};

export default PlanComparisonTable;
